import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { paymentConfig } from '@/lib/paymentConfig';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, Lock, Globe, Loader2, Check } from 'lucide-react';

type BillingCycle = 'monthly' | 'yearly';

interface LocationState {
  plan: 'free' | 'basic' | 'intermediate' | 'advanced';
  billingCycle?: BillingCycle;
  provider: 'stripe' | 'paystack';
  currency: string;
}

// Paystack inline-JS types (loaded dynamically via script tag)
interface PaystackHandler {
  openIframe: () => void;
}
interface PaystackPopSetupOptions {
  key: string;
  email: string;
  amount: number;
  currency: string;
  ref: string;
  metadata?: Record<string, unknown>;
  onSuccess: (transaction: { reference: string; transaction: string }) => void;
  onCancel: () => void;
}
interface PaystackPopStatic {
  setup: (options: PaystackPopSetupOptions) => PaystackHandler;
}

declare global {
  interface Window {
    PaystackPop?: PaystackPopStatic;
  }
}

// Regional pricing (monthly base prices)
const regionalPricing: Record<string, Record<string, { price: number; currency: string; code: string }>> = {
  NGN: {
    basic:        { price: 2500,  currency: '₦',   code: 'NGN' },
    intermediate: { price: 5000,  currency: '₦',   code: 'NGN' },
    advanced:     { price: 7500,  currency: '₦',   code: 'NGN' },
  },
  GHS: {
    basic:        { price: 30,  currency: 'GH₵', code: 'GHS' },
    intermediate: { price: 60,  currency: 'GH₵', code: 'GHS' },
    advanced:     { price: 90,  currency: 'GH₵', code: 'GHS' },
  },
  KES: {
    basic:        { price: 350,  currency: 'KSh', code: 'KES' },
    intermediate: { price: 700,  currency: 'KSh', code: 'KES' },
    advanced:     { price: 1050, currency: 'KSh', code: 'KES' },
  },
  ZAR: {
    basic:        { price: 50,  currency: 'R', code: 'ZAR' },
    intermediate: { price: 100, currency: 'R', code: 'ZAR' },
    advanced:     { price: 150, currency: 'R', code: 'ZAR' },
  },
};

const euroPricing: Record<string, { price: number; currency: string; code: string }> = {
  basic:        { price: 5,  currency: '€', code: 'EUR' },
  intermediate: { price: 10, currency: '€', code: 'EUR' },
  advanced:     { price: 15, currency: '€', code: 'EUR' },
};

const YEARLY_MULTIPLIER = 10; // 10 months price = 2 months free

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrganization } = useAppStore();

  const state = location.state as LocationState;
  const provider = state?.provider;
  const billingCycle: BillingCycle = state?.billingCycle ?? 'monthly';
  const isYearly = billingCycle === 'yearly';

  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Stripe-only form states
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardName, setCardName] = useState('');

  // Shared form states
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');

  // Get base (monthly) pricing entry
  const getMonthlyPricing = () => {
    if (state?.currency && regionalPricing[state.currency]?.[state.plan]) {
      return regionalPricing[state.currency][state.plan];
    }
    return euroPricing[state?.plan || 'basic'];
  };

  const monthlyPricing = getMonthlyPricing();
  const chargeAmount = isYearly
    ? monthlyPricing.price * YEARLY_MULTIPLIER
    : monthlyPricing.price;

  // Load Paystack inline JS script once when provider is paystack
  useEffect(() => {
    if (provider !== 'paystack') return;
    if (document.getElementById('paystack-inline-js')) return;
    const script = document.createElement('script');
    script.id = 'paystack-inline-js';
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    document.head.appendChild(script);
  }, [provider]);

  useEffect(() => {
    if (!state?.plan) {
      navigate('/subscription');
    }
  }, [state, navigate]);

  // Persist a verified payment to Supabase and mark success
  const saveSubscription = useCallback(
    async (paymentReference: string) => {
      if (!currentOrganization || !user) return;
      try {
        const now = new Date();
        const expiresAt = new Date(now);
        if (isYearly) {
          expiresAt.setFullYear(expiresAt.getFullYear() + 1);
        } else {
          expiresAt.setMonth(expiresAt.getMonth() + 1);
        }

        const { error: subError } = await supabase
          .from('subscriptions')
          .insert({
            organization_id: currentOrganization.id,
            plan: state.plan,
            status: 'active',
            amount: chargeAmount,
            currency: monthlyPricing.code,
            payment_provider: provider,
            payment_reference: paymentReference,
            billing_cycle: billingCycle,
            current_period_start: now.toISOString(),
            current_period_end: expiresAt.toISOString(),
            cancel_at_period_end: false,
          });

        if (subError) throw subError;

        const maxUsers = state.plan === 'basic' ? 50 : state.plan === 'intermediate' ? 80 : 200;
        const { error: orgError } = await supabase
          .from('organizations')
          .update({
            subscription_plan: state.plan,
            subscription_status: 'active',
            subscription_expires_at: expiresAt.toISOString(),
            max_users: maxUsers,
            member_portal_enabled: state.plan === 'intermediate' || state.plan === 'advanced',
          })
          .eq('id', currentOrganization.id);

        if (orgError) throw orgError;

        setIsSuccess(true);
        toast.success('Payment successful! Your subscription is now active.');
        setTimeout(() => navigate('/dashboard'), 3000);
      } catch (error) {
        console.error('Subscription save error:', error);
        toast.error('Payment received but subscription activation failed. Please contact support.');
      } finally {
        setIsProcessing(false);
      }
    },
    [
      billingCycle, chargeAmount, currentOrganization, isYearly,
      monthlyPricing.code, navigate, provider, state?.plan, user,
    ],
  );

  // ── Paystack: open inline popup ──────────────────────────────────────────────
  const handlePaystackPayment = (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrganization || !user) {
      toast.error('Please sign in and select an organization');
      return;
    }

    if (!window.PaystackPop) {
      toast.error('Paystack is still loading. Please try again in a moment.');
      return;
    }

    setIsProcessing(true);

    const ref = `TICKALLY-SUB-${currentOrganization.id.slice(0, 8).toUpperCase()}-${Date.now()}`;

    const handler = window.PaystackPop.setup({
      key: paymentConfig.paystack.publicKey,
      email,
      // Paystack expects amount in the smallest currency unit (kobo for NGN, pesewas for GHS, etc.)
      amount: chargeAmount * 100,
      currency: monthlyPricing.code,
      ref,
      metadata: {
        organization_id: currentOrganization.id,
        plan: state.plan,
        billing_cycle: billingCycle,
      },
      onSuccess: (transaction) => {
        void saveSubscription(transaction.reference);
      },
      onCancel: () => {
        setIsProcessing(false);
        toast.info('Payment cancelled.');
      },
    });

    handler.openIframe();
  };

  // ── Stripe: manual card form (to be upgraded to Stripe Elements) ─────────────
  const handleStripePayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrganization || !user) {
      toast.error('Please sign in and select an organization');
      return;
    }

    setIsProcessing(true);

    try {
      // TODO: Replace with Stripe Elements / PaymentIntent flow
      await new Promise(resolve => setTimeout(resolve, 2000));
      const mockRef = `STRIPE-MOCK-${Date.now()}`;
      await saveSubscription(mockRef);
    } catch (error) {
      console.error('Stripe payment error:', error);
      toast.error('Payment failed. Please try again.');
      setIsProcessing(false);
    }
  };

  const getProviderName = () => {
    switch (provider) {
      case 'stripe':   return 'Stripe';
      case 'paystack': return 'Paystack';
      default:         return 'Payment Provider';
    }
  };

  const getProviderIcon = () => {
    switch (provider) {
      case 'stripe':   return <CreditCard className="w-5 h-5" />;
      case 'paystack': return <Globe className="w-5 h-5" />;
      default:         return <CreditCard className="w-5 h-5" />;
    }
  };

  if (isSuccess) {
    return (
      <div className="max-w-md mx-auto py-12">
        <Card className="text-center">
          <CardContent className="pt-12 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground mb-6">
              Your {state.plan} {billingCycle} subscription is now active. You will be redirected to the dashboard shortly.
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8">
      <Button
        variant="ghost"
        className="mb-4"
        onClick={() => navigate('/subscription')}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Plans
      </Button>

      <div className="grid md:grid-cols-5 gap-6">
        {/* Payment Form */}
        <div className="md:col-span-3">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                {getProviderIcon()}
                <CardTitle>Pay with {getProviderName()}</CardTitle>
              </div>
              <CardDescription>
                {provider === 'paystack'
                  ? 'Enter your details and you will be redirected to Paystack to complete payment securely.'
                  : 'Enter your payment details to complete the subscription.'}
              </CardDescription>
            </CardHeader>
            <CardContent>

              {/* ── PAYSTACK FORM ── */}
              {provider === 'paystack' && (
                <form onSubmit={handlePaystackPayment} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+234 801 234 5678"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Opening Paystack…
                      </>
                    ) : (
                      <>
                        <Globe className="w-4 h-4 mr-2" />
                        Pay {monthlyPricing.currency}{chargeAmount.toLocaleString()} with Paystack
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" />
                    Secured by Paystack — your card details never touch our servers
                  </p>
                </form>
              )}

              {/* ── STRIPE FORM ── */}
              {provider === 'stripe' && (
                <form onSubmit={handleStripePayment} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <Label htmlFor="cardName">Name on Card</Label>
                    <Input
                      id="cardName"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                      placeholder="John Doe"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cardNumber">Card Number</Label>
                    <Input
                      id="cardNumber"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(e.target.value)}
                      placeholder="4242 4242 4242 4242"
                      maxLength={19}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expiry">Expiry Date</Label>
                      <Input
                        id="expiry"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        placeholder="MM/YY"
                        maxLength={5}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cvv">CVV</Label>
                      <Input
                        id="cvv"
                        value={cvv}
                        onChange={(e) => setCvv(e.target.value)}
                        placeholder="123"
                        maxLength={4}
                        required
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing…
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Pay {monthlyPricing.currency}{chargeAmount.toLocaleString()}
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" />
                    Your payment is secured with Stripe
                  </p>
                </form>
              )}

            </CardContent>
          </Card>
        </div>

        {/* Order Summary */}
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-medium capitalize">{state?.plan}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Billing</span>
                <div className="flex items-center gap-1.5">
                  <span className="font-medium capitalize">{billingCycle}</span>
                  {isYearly && (
                    <Badge className="bg-green-500 text-white text-xs py-0 px-1.5">
                      2 months free
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Currency</span>
                <span className="font-medium">{monthlyPricing.code}</span>
              </div>

              <Separator />

              <div className="flex justify-between text-lg">
                <span className="font-medium">Total</span>
                <div className="text-right">
                  <span className="font-bold">
                    {monthlyPricing.currency}{chargeAmount.toLocaleString()}/{isYearly ? 'yr' : 'mo'}
                  </span>
                  {isYearly && (
                    <p className="text-xs text-muted-foreground">
                      ({monthlyPricing.currency}{monthlyPricing.price}/mo equivalent)
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-muted p-3 rounded-lg text-sm space-y-2">
                <p className="font-medium">What's included:</p>
                <ul className="space-y-1 text-muted-foreground">
                  {state?.plan === 'basic' && (
                    <>
                      <li>• Up to 50 members</li>
                      <li>• Dues tracking</li>
                      <li>• Donations management</li>
                    </>
                  )}
                  {state?.plan === 'intermediate' && (
                    <>
                      <li>• Up to 80 members</li>
                      <li>• Member Portal</li>
                      <li>• Join code for members</li>
                    </>
                  )}
                  {state?.plan === 'advanced' && (
                    <>
                      <li>• Up to 200 members</li>
                      <li>• Advanced analytics</li>
                      <li>• Dedicated support</li>
                    </>
                  )}
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
